import { mapGetters } from 'vuex';
import * as queries from 'lib/graphql/queries';
import storefront from 'lib/storefront';
import ClickOutside from 'vue-click-outside';
import {
  keysToCamel,
  handleize,
  convertGQLToProductJson,
  getValueForTag,
  getImageWithSize,
} from 'utils/general';

export default {
  components: {
    'transition-expand': () =>
      import('../transition-expand/transition-expand.vue'),
  },
  props: {
    inlineProduct: Object,
    inlineQuickViewOpen: Boolean,
    inlineSwatches: Boolean,
    isGwpCollectionProduct: Boolean,
    isGWP: Boolean,
  },
  directives: {
    ClickOutside,
  },
  data() {
    return {
      selectedVariant: {},
      microCollectionProducts: [],
      parentProductTitle: '',
      dropdownActive: false,
      isGWPSelected: false,
    };
  },
  watch: {
    inlineProduct: {
      async handler(newData) {
        const baseTitle = handleize(
          newData.title?.split(__GLOBAL__.productDelimiter)[0],
        );
        await this.fetchMicroCollection(baseTitle);

        const selectedProduct =
          this.variantSwatches.find((product) =>
            product.variants.some((variant) =>
              this.rewardUserList.includes(Number(variant.id)),
            ),
          ) ||
          this.microCollectionProducts.find((product) => product.available);

        if (!selectedProduct) return;

        this.selectedVariant = keysToCamel(selectedProduct.variants[0]);
        this.parentProductTitle = selectedProduct.title;

        if (this.inlineSwatches) {
          this.dispatchProductPayload(selectedProduct, true);
        }
      },
      deep: true,
      immediate: true,
    },
    inlineQuickViewOpen(isOpen) {
      if (!isOpen) return;
      const firstAvailable = this.microCollectionProducts.find(
        (product) => product.available,
      );
      this.dispatchProductPayload(firstAvailable);
    },
  },
  computed: {
    ...mapGetters('global', ['rewardUserList']),
    variantSwatches() {
      const swatchProducts = this.microCollectionProducts.map((variant) => {
        const colorTag = variant.tags.find((tag) => tag.includes('Color ID'));
        const colorId = colorTag ? colorTag.split('|')[1].toLowerCase() : '';
        return {
          ...variant,
          colorClass: `fenty-color-${colorId}`,
        };
      });

      const selectedProduct = swatchProducts.find((product) =>
        product.variants.some((variant) =>
          this.rewardUserList.includes(Number(variant.id)),
        ),
      );

      if (this.isGWP && this.rewardUserList) {
        if (selectedProduct) {
          this.selectedVariant = keysToCamel(selectedProduct.variants[0]);
          this.isGWPSelected = true;
        } else {
          this.isGWPSelected = false;
        }
        return selectedProduct ? [selectedProduct] : swatchProducts;
      }

      return swatchProducts;
    },
    selectedColorClass() {
      const selectedProduct = this.microCollectionProducts.find((product) =>
        product.variants.find((variant) => {
          if (variant.id === this.selectedVariant.id) {
            return true;
          }

          return false;
        }),
      );

      if (!selectedProduct) {
        return '';
      }

      let colorId = '';
      const colorTag = selectedProduct.tags.find((tag) =>
        tag.includes('Color ID'),
      );
      if (colorTag) {
        colorId = colorTag.split('|')[1].toLowerCase();
      }

      return `fenty-color-${colorId}`;
    },
    selectedVariantTitle() {
      return this.selectedVariant.title.includes('/') ? this.selectedVariant.title.split('/')[0].trim() : this.selectedVariant.title;
    }
  },
  methods: {
    async fetchMicroCollection(baseTitle) {
      if (!baseTitle) return;
      let normalizedProducts;
      const handle = baseTitle.replace(/-plus;/g, '');
      const response = await storefront.request(
        queries.collectionWithVariants,
        { handle },
      );
      const products = response?.collection?.products?.edges;

      if (this.isGWP) {
        normalizedProducts = products
          ?.map((product) => {
            const convertedProduct = convertGQLToProductJson(product.node);
            const filteredVariants = convertedProduct.variants.filter(
              (variant) => variant.isGWPVariant,
            );
            return { ...convertedProduct, variants: filteredVariants };
          })
          .filter((p) => p.variants.length);
      } else if (this.isGwpCollectionProduct) {
        normalizedProducts = products
          ?.reduce((arr, product) => {
            const convertedProduct = convertGQLToProductJson(product.node);
            const filteredVariants = convertedProduct.variants?.filter(
              (variant) => variant && variant.sourceGWPCollectionItem,
            );
            if (filteredVariants?.length) {
              arr.push({ ...convertedProduct, variants: filteredVariants });
            }
            return arr;
          }, [])
          .filter(Boolean);
      } else {
        normalizedProducts = products?.map((product) =>
          convertGQLToProductJson(product.node),
        );
      }

      if (!normalizedProducts) return;
      this.microCollectionProducts = normalizedProducts;
    },
    hide(e) {
      if (
        e.target.closest('.product-card') === this.$el.closest('.product-card')
      )
        return;
      this.dropdownActive = false;
      this.$parent.$emit('inline-quick-view:close-quick-view');
    },
    changeProduct(variant) {
      this.dispatchProductPayload(variant);
      setTimeout(() => {
        this.dropdownActive = false;
        this.$parent.$emit('inline-quick-view:close-quick-view');
      }, 200);
    },
    toggleDropdown() {
      if (this.dropdownActive) {
        this.dropdownActive = false;
        this.$parent.$emit('inline-quick-view:close-quick-view');
      } else {
        this.dropdownActive = true;
        this.$parent.$emit('inline-quick-view:open-quick-view');
      }
    },
    dispatchProductPayload(product) {
      this.selectedVariant = keysToCamel(product.variants[0]);
      const productVariation = getValueForTag('Color ID', product.tags);
      const payload = {
        id: this.selectedVariant.id,
        quantity: 1,
        sku: this.selectedVariant.sku,
        price: this.selectedVariant.price,
        productTitle: product.title,
        variantTitle: this.selectedVariant.title,
        image: product.featuredImage,
        media: product.media,
        productVariation,
        product_id: product.id,
        handle: product.handle,
        available: product.available,
        optionNames: product.optionNames,
        product: product
      };
      if (this.inlineSwatches) {
        payload.product = product;
        payload.variants = product.variants;
      }
      this.$parent.$emit('inline-quick-view:shade-selected', payload);
    },
    getVariantTitle(variant) {
      if (variant.Color && variant.Color[0]) {
        return variant.Color[0];
      }

      if (
        variant?.optionsWithValues?.Color &&
        variant?.optionsWithValues?.Color[0]
      ) {
        return variant.optionsWithValues.Color[0];
      }

      return variant.variants[0].title;
    },
    preloadHoverImage(variant) {
      const hoverImage = new Image();
      hoverImage.src = getImageWithSize(variant?.media[1]?.src);
    },
    getShadeTitle(variant) {
      return variant.title.includes(__GLOBAL__.productDelimiter)
        ? variant.title.split(__GLOBAL__.productDelimiter)[1].trim()
        : variant.title;
    },
  },
  template: `
  <div
    class="product-card__swatches"
    :class="{'disabled' : isGWPSelected}"
    :inline-product="inlineProduct"
    v-if="inlineSwatches"
    v-show="microCollectionProducts?.length > 0"
    @click="toggleDropdown"
    @keyup.enter="toggleDropdown"
    v-click-outside="hide"
    tabindex="0" role="select">

    <div class="product-card__swatches-summary pointer">
      <span class="product-card__swatches-color" :class="[selectedColorClass]"></span>
      <span
        class="product-card__swatches-title"
        v-html="selectedVariantTitle"
      ></span>
      <template v-if="isGWP || isGwpCollectionProduct">
        <svg class="icon-minus" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" width="17" height="17" v-show="dropdownActive">
          <path d="M3 8.68774H13.8182" stroke="black" stroke-width="1.4"/>
        </svg>
        <svg class="icon-plus" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" width="17" height="17" v-show="!dropdownActive">
          <path d="M3 8.68781H13.8182" stroke="black" stroke-width="1.4"/>
          <path d="M8.40967 14.3746L8.40967 3.00003" stroke="black" stroke-width="1.4"/>
        </svg>
      </template>
      <template v-else>
        <svg xmlns="http://www.w3.org/2000/svg" width="44" height="38" viewBox="0 0 44 38" fill="none">
          <path d="M22.3479 23.7491C22.1473 23.9776 21.791 23.9758 21.5928 23.7452L19.3548 21.1403C19.2155 20.9781 19.3307 20.7273 19.5444 20.7273H24.448C24.6629 20.7273 24.7776 20.9807 24.6358 21.1422L22.3479 23.7491Z" fill="black"/>
          <path d="M22.3479 14.2509C22.1473 14.0224 21.791 14.0242 21.5928 14.2548L19.3548 16.8597C19.2155 17.0219 19.3307 17.2727 19.5444 17.2727H24.448C24.6629 17.2727 24.7776 17.0193 24.6358 16.8578L22.3479 14.2509Z" fill="black"/>
        </svg>
      </template>
    </div>

    <div class="inline-quick-view"
      :class="{ 'inline-quick-view--gwp' : (isGWP || isGwpCollectionProduct)}"
      :inline-product="inlineProduct"
      :inline-quick-view-open="dropdownActive"
      v-show="dropdownActive">
      <button
        v-for="(variant, index) in variantSwatches"
        class="inline-quick-view__variant"
        :class="{
          active: getVariantTitle(variant) === selectedVariant.title,
          unavailable: !variant.available
        }"
        :key="index"
        :disabled="!variant.available"
        role="radio"
        tabindex="0"
        @click.prevent.stop="changeProduct(variant)"
        @mouseover="preloadHoverImage(variant)"
        :aria-label="variant.title"
        type="button">
          <div class="inline-quick-view__variant-swatch" :class="[
            variant.colorClass,
          ]"></div>
          <span
            class="product-card__swatches-title"
            v-html="getShadeTitle(variant)"
            v-if="!isGWP && !isGwpCollectionProduct"
          ></span>
        </button>
      </div>
    </div>
  `,
};
